import {
    Typography,
    Box,
    IconButton,
    useTheme,
    AppBar,
    Container,
    Button,
    Drawer,
    Stack,
    useMediaQuery,
    useScrollTrigger
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useContext, useEffect, useState } from "react";
import { ColorModeContext, tokens } from "../../theme";
import { EASE } from "../../styles/surfaces";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import React from "react";
import logo from './logo.png';
import { Link, useLocation } from "react-router-dom";

const Header = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const colorMode = useContext(ColorModeContext);
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [drawerOpen, setDrawerOpen] = useState(false);

    // The bar is transparent-ish at the top of the page and only grows an edge once
    // content has started sliding under it. `disableHysteresis` so it tracks position
    // rather than scroll direction — this is about what is behind the bar, not which way
    // the visitor is going.
    const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

    // A route change while the drawer is open would otherwise leave it covering the new
    // page. The nav links navigate rather than calling a handler, so close on the path.
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    const menuItems = [
        { text: 'Home', path: '/' },
        { text: 'Projects', path: '/projects' },
        { text: 'About', path: '/about' },
        { text: 'Contact', path: '/contact' },
    ];

    const colorModeToggle = (
        <IconButton
            onClick={colorMode.toggleColorMode}
            aria-label={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            sx={{
                color: theme.palette.text.primary,
                transition: `background-color .2s ${EASE}, color .2s ${EASE}`,
                '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
            }}
        >
            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
    );

    return (
        <AppBar
            position="sticky"
            elevation={0}
            color="inherit"
            sx={{
                // Frosted rather than solid, so the page reads as continuing underneath.
                // The @supports fallback matters: without backdrop-filter the alpha would
                // leave content showing straight through the bar.
                backgroundColor: colors.background[200],
                borderBottom: '1px solid',
                borderColor: scrolled ? theme.palette.divider : 'transparent',
                backgroundImage: 'none',
                transition: `border-color .3s ${EASE}, box-shadow .3s ${EASE}`,
                boxShadow: scrolled
                    ? `0 6px 20px -12px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,.8)' : 'rgba(16,24,40,.28)'}`
                    : 'none',
                '@supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))': {
                    backgroundColor: alpha(colors.background[200], 0.82),
                    backdropFilter: 'blur(14px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(14px) saturate(180%)',
                },
            }}
        >
            <Container maxWidth="xl">
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={1.5}
                    position="relative"
                >
                    {/* Logo */}
                    <Box
                        component={Link}
                        to="/"
                        onClick={() => window.scrollTo(0, 0)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            height: { xs: '45px', md: '58px' },
                        }}
                    >
                        <img
                            src={logo}
                            alt="Logo"
                            style={{
                                height: "100%",
                                width: "auto",
                                objectFit: "contain",
                                maxWidth: "200px",
                            }}
                        />
                    </Box>

                    {/* Desktop Navigation */}
                    {!isMobile && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                        >
                            {menuItems.map((item) => (
                                <Button
                                    key={item.text}
                                    component={Link}
                                    to={item.path}
                                    onClick={() => window.scrollTo(0, 0)}
                                    disableRipple
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        color: location.pathname === item.path
                                            ? theme.palette.primary.main
                                            : theme.palette.text.primary,
                                        fontSize: '0.95rem',
                                        fontWeight: location.pathname === item.path ? 600 : 500,
                                        position: 'relative',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            width: location.pathname === item.path ? '24px' : '0px',
                                            height: '2px',
                                            borderRadius: '2px',
                                            bottom: 4,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: theme.palette.primary.main,
                                            transition: `width .3s ${EASE}`,
                                        },
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                            color: theme.palette.primary.main,
                                            '&::after': {
                                                width: '24px',
                                            },
                                        },
                                    }}
                                >
                                    {item.text}
                                </Button>
                            ))}
                            <Box sx={{ width: '1px', height: 24, mx: 1, backgroundColor: theme.palette.divider }} />
                            {colorModeToggle}
                        </Box>
                    )}

                    {/* Mobile Navigation */}
                    {isMobile && (
                        <>
                            {/* The wordmark is centred absolutely, so on the narrowest
                                phones it ran into the logo on one side and the buttons on
                                the other. The logo already carries the name — below `sm`
                                it is the only mark shown. */}
                            <Typography
                                variant="h6"
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    color: theme.palette.text.primary,
                                    fontWeight: 700,
                                    fontSize: '1.35rem',
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    fontFamily: 'inherit',
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        width: '40%',
                                        height: '2px',
                                        borderRadius: '2px',
                                        bottom: -4,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: theme.palette.primary.main
                                    }
                                }}
                            >
                                AV FACADE
                            </Typography>

                            <Box display="flex" alignItems="center" gap={0.5}>
                                {colorModeToggle}
                                <IconButton
                                    size="large"
                                    edge="end"
                                    onClick={() => setDrawerOpen(true)}
                                    aria-label="Open navigation menu"
                                    aria-expanded={drawerOpen}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        padding: 1
                                    }}
                                >
                                    <MenuIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                            </Box>

                            <Drawer
                                anchor="right"
                                open={drawerOpen}
                                onClose={() => setDrawerOpen(false)}
                                PaperProps={{
                                    sx: {
                                        width: { xs: '78vw', sm: 320 },
                                        maxWidth: 360,
                                        backgroundColor: colors.background[200],
                                        backgroundImage: 'none',
                                        borderRadius: 0,
                                        borderLeft: `1px solid ${theme.palette.divider}`,
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        px: 2.5,
                                        py: 2,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <IconButton
                                        onClick={() => setDrawerOpen(false)}
                                        aria-label="Close navigation menu"
                                        sx={{ color: theme.palette.text.primary }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>

                                <Stack component="nav" sx={{ p: 1.5 }} spacing={0.5}>
                                    {menuItems.map((item) => {
                                        const active = location.pathname === item.path;
                                        return (
                                            <Box
                                                key={item.text}
                                                component={Link}
                                                to={item.path}
                                                onClick={() => window.scrollTo(0, 0)}
                                                aria-current={active ? 'page' : undefined}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    // 56px keeps every row above the 44px
                                                    // minimum touch target with room to spare.
                                                    minHeight: 56,
                                                    px: 2,
                                                    borderRadius: 2.5,
                                                    textDecoration: 'none',
                                                    fontSize: '1.0625rem',
                                                    fontWeight: active ? 700 : 500,
                                                    color: active
                                                        ? theme.palette.primary.main
                                                        : theme.palette.text.primary,
                                                    backgroundColor: active
                                                        ? alpha(theme.palette.primary.main, 0.1)
                                                        : 'transparent',
                                                    transition: `background-color .2s ${EASE}, color .2s ${EASE}`,
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.14),
                                                        color: theme.palette.primary.main,
                                                    },
                                                }}
                                            >
                                                {item.text}
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Drawer>
                        </>
                    )}
                </Box>
            </Container>
        </AppBar>
    );
};

export default Header;
